import { OptionallyVisible } from 'components';
import { useTranslation } from 'react-i18next';
import { MerchantInfo } from 'services/merchants/types';

import { FormLayout } from 'components/form/formLayout/formLayout';
import { FormFieldDescription } from 'components/form/formLayout/types';
import IconDataBase from 'components/icons/dataBase';

import { getValidationErrorMessage } from 'utils/error';

import { hasKycStarted } from '../../utils/hasKycStarted';
import { FormType, LABELS } from '../keys';
import { AutoKybProps } from '../kyb';
import { ErrorMessage, PanelButton, PanelButtonsWrapper, TitanDataStatusBadge } from '../styles';

interface GeneralAccountSettingFormProps {
  openModal: () => void;
  openAssociatedPersonModal: () => void;
  merchant: MerchantInfo;
  isKyb: boolean;
  autoKybProps: AutoKybProps;
  titanFormData?: FormType;
  isLoadingTitanData?: boolean;
}

export const KybPanel = ({
  openModal,
  openAssociatedPersonModal,
  isKyb,
  merchant,
  autoKybProps,
  titanFormData,
  isLoadingTitanData,
}: GeneralAccountSettingFormProps) => {
  const { run, isProcessing } = autoKybProps;
  const { t } = useTranslation();

  const shouldHideBridgeKybButton = hasKycStarted(merchant.kyc?.kycStatus) || merchant.acceptedTOS.length === 0;

  const fields: FormFieldDescription[] = [
    {
      key: t(LABELS.PANEL_FIELDS.INFORMATION),
      label: t(LABELS.PANEL_FIELDS.INFORMATION),
      valueLabelWidth: '446px',
      hidden: !isKyb || shouldHideBridgeKybButton,
      renderInput: () => t(LABELS.PANEL_VALUES.INFORMATION),
    },
    {
      key: t(LABELS.PANEL_FIELDS.BUTTON),
      label: t(LABELS.PANEL_FIELDS.BUTTON),
      hidden: !isKyb || shouldHideBridgeKybButton,
      renderInput: () => (
        <>
          <PanelButtonsWrapper>
            <PanelButton disabled={merchant.acceptedTOS.length === 0 || isLoadingTitanData} onClick={openModal}>
              {isLoadingTitanData ? t(LABELS.PANEL_VALUES.LOADING) : t(LABELS.PANEL_VALUES.BRIDGE_KYB_BUTTON)}
            </PanelButton>
            <PanelButton onClick={openAssociatedPersonModal}>{t(LABELS.PANEL_VALUES.ASSOCIATED_PERSON_BUTTON)}</PanelButton>
            <OptionallyVisible visible={Boolean(merchant.shift4MerchantId) && !titanFormData}>
              <PanelButton secondary onClick={run} disabled={isProcessing}>
                {t(LABELS.PANEL_VALUES.TITAN_AUTO_KYB_BUTTON)}
              </PanelButton>
            </OptionallyVisible>
            <OptionallyVisible visible={Boolean(titanFormData)}>
              <TitanDataStatusBadge>
                <IconDataBase /> {t(LABELS.PANEL_VALUES.TITAN_DATA_AVAILABLE)}
              </TitanDataStatusBadge>
            </OptionallyVisible>
          </PanelButtonsWrapper>
          <OptionallyVisible visible={Boolean(merchant.kyc?.autoKycErrors)}>
            <ErrorMessage>{getValidationErrorMessage(merchant.kyc?.autoKycErrors)}</ErrorMessage>
          </OptionallyVisible>
        </>
      ),
    },
  ];

  return <FormLayout fields={fields} />;
};
